// src/components/Dashboard/PlayerDashboard/MyNextUpWidget.jsx
// Hero section showing the next upcoming event with countdown and RSVP

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { 
  FaCalendarAlt, FaClock, FaMapMarkerAlt, FaBuilding,
  FaCheckCircle, FaTimesCircle, FaQuestionCircle, FaArrowRight,
  FaPlay, FaExternalLinkAlt, FaDirections
} from 'react-icons/fa';
import { rsvpApi } from '../../../api/rsvp';
import styles from './MyNextUpWidget.module.css';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const MyNextUpWidget = ({ events, selectedProgram, refreshTrigger }) => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState('');
  const [userRsvp, setUserRsvp] = useState(null);
  const [rsvpLoading, setRsvpLoading] = useState(false);

  // Get the next upcoming event
  const nextEvent = useMemo(() => {
    if (!events || events.length === 0) return null;
    
    const now = new Date();
    const upcoming = events
      .filter(e => new Date(e.dateTime?.start) > now || e.status === 'Live')
      .sort((a, b) => {
        // Live events first
        if (a.status === 'Live' && b.status !== 'Live') return -1;
        if (b.status === 'Live' && a.status !== 'Live') return 1;
        return new Date(a.dateTime?.start) - new Date(b.dateTime?.start);
      });
    
    return upcoming[0] || null;
  }, [events]);

  // Normalize RSVP response to consistent format
  const normalizeRsvp = (response) => {
    if (!response) return null;
    const lower = response.toLowerCase();
    if (lower === 'going' || lower === 'yes' || lower === 'attend') return 'going';
    if (lower === 'notgoing' || lower === 'not going' || lower === 'no' || lower === 'decline') return 'notGoing';
    if (lower === 'tbd' || lower === 'maybe' || lower === 'tentative') return 'tbd';
    return response;
  };

  // Initialize RSVP from event data and fetch if not present
  useEffect(() => {
    if (!nextEvent) {
      setUserRsvp(null);
      return;
    }

    // Check if event already has userRsvp data
    if (nextEvent.userRsvp) {
      setUserRsvp(normalizeRsvp(nextEvent.userRsvp));
      return;
    }

    // Otherwise fetch from API using rsvpApi
    const fetchRsvp = async () => {
      try {
        const auth = getAuth();
        if (!auth.currentUser) return;

        const data = await rsvpApi.getUserRSVP(nextEvent._id);
        const rsvpValue = data.response || data.rsvp?.response || data.status;
        if (rsvpValue) {
          setUserRsvp(normalizeRsvp(rsvpValue));
        }
      } catch (error) {
        // Silently fail - user might not have RSVP'd yet
      }
    };

    fetchRsvp();
  }, [nextEvent, refreshTrigger]);

  // Update countdown timer
  useEffect(() => {
    if (!nextEvent || nextEvent.status === 'Live') {
      setCountdown('');
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const eventTime = new Date(nextEvent.dateTime?.start);
      const diff = eventTime - now;

      if (diff <= 0) {
        setCountdown('Starting now!');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (days > 0) {
        setCountdown(`in ${days}d ${hours}h`);
      } else if (hours > 0) {
        setCountdown(`in ${hours}h ${minutes}m`);
      } else {
        setCountdown(`in ${minutes}m`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);
    return () => clearInterval(interval);
  }, [nextEvent]);

  // Convert internal format to API format
  const toApiRsvpFormat = (response) => {
    switch (response) {
      case 'going': return 'Going';
      case 'notGoing': return 'Not Going';
      case 'tbd': return 'TBD';
      default: return response;
    }
  };

  // Handle RSVP
  const handleRsvp = async (response) => {
    if (!nextEvent?._id || rsvpLoading) return;

    setRsvpLoading(true);
    try {
      // Convert to API format (Going, Not Going, TBD)
      const apiResponse = toApiRsvpFormat(response);
      
      await rsvpApi.submitRSVP(nextEvent._id, apiResponse);
      setUserRsvp(response);
    } catch (error) {
      console.error('Error updating RSVP:', error);
    } finally {
      setRsvpLoading(false);
    }
  };

  const handleViewEvent = () => {
    if (nextEvent) {
      navigate(`/program/${nextEvent.programId}/events?eventId=${nextEvent._id}`);
    }
  };

  const handleViewAllEvents = () => {
    if (selectedProgram) {
      navigate(`/program/${selectedProgram._id}/events`);
    } else if (nextEvent) {
      navigate(`/program/${nextEvent.programId}/events`);
    }
  };

  const handleProgramClick = (e) => {
    e.stopPropagation();
    if (nextEvent?.programId) {
      navigate(`/program/${nextEvent.programId}`);
    }
  };

  const handleClubClick = (e) => {
    e.stopPropagation();
    const clubId = nextEvent?.clubId?._id || nextEvent?.clubId;
    if (clubId) {
      navigate(`/club/${clubId}`);
    }
  };

  const formatTime = (dateTime) => {
    const date = new Date(dateTime);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const formatDate = (dateTime) => {
    const date = new Date(dateTime);
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  };

  const getEventTypeIcon = (eventType) => {
    switch (eventType?.toLowerCase()) {
      case 'game':
      case 'match':
        return '🏆';
      case 'practice':
      case 'training':
        return '⚽';
      case 'meeting':
        return '📋';
      default:
        return '📅';
    }
  };

  const isLive = nextEvent?.status === 'Live';

  // Extract location coordinates
  const location = nextEvent?.location;
  const coordinatesArray = location?.address?.coordinates?.coordinates;
  const hasValidCoordinates = Array.isArray(coordinatesArray) && coordinatesArray.length >= 2;
  const lat = hasValidCoordinates ? coordinatesArray[1] : null;
  const lng = hasValidCoordinates ? coordinatesArray[0] : null;

  // Build Google Maps URL for directions
  const getDirectionsUrl = () => {
    if (hasValidCoordinates) {
      return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    }
    if (location?.name) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.name)}`;
    }
    return null;
  };

  // Empty state
  if (!nextEvent) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <FaCalendarAlt className={styles.headerIcon} />
            <h2 className={styles.headerTitle}>My Next Up</h2>
          </div>
        </div>
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon}>📅</span>
          <p>No upcoming events</p>
          <span className={styles.emptyHint}>Events will appear here when scheduled</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.container} ${isLive ? styles.live : ''}`}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <FaCalendarAlt className={styles.headerIcon} />
          <h2 className={styles.headerTitle}>My Next Up</h2>
          {isLive && (
            <span className={styles.liveBadge}>
              <FaPlay /> LIVE NOW
            </span>
          )}
        </div>
        {!isLive && countdown && (
          <span className={styles.countdown}>
            <FaClock /> {countdown}
          </span>
        )}
      </div>

      <div className={styles.content}>
        <div className={styles.eventCard}>
          {/* Event Type & Title */}
          <div className={styles.eventHeader}>
            <span className={styles.eventIcon}>{getEventTypeIcon(nextEvent.type)}</span>
            <span className={styles.eventType}>{nextEvent.type || 'Event'}</span>
          </div>

          <h3 className={styles.eventTitle}>{nextEvent.title}</h3>

          {/* Program & Club Links */}
          <div className={styles.contextLinks}>
            {nextEvent.programName && (
              <button 
                className={styles.programLink}
                onClick={handleProgramClick}
                title="View Program"
              >
                <FaExternalLinkAlt className={styles.linkIcon} />
                {nextEvent.programName}
              </button>
            )}
            {(nextEvent.clubName || nextEvent.clubId?.name) && (
              <button 
                className={styles.clubLink}
                onClick={handleClubClick}
                title="View Club"
              >
                <FaBuilding className={styles.linkIcon} />
                {nextEvent.clubName || nextEvent.clubId?.name}
              </button>
            )}
          </div>

          {/* Date & Time */}
          <div className={styles.eventMeta}>
            <div className={styles.metaItem}>
              <FaCalendarAlt />
              <span>{formatDate(nextEvent.dateTime?.start)}</span>
            </div>
            <div className={styles.metaItem}>
              <FaClock />
              <span>{formatTime(nextEvent.dateTime?.start)}</span>
            </div>
            {location?.name && (
              <div className={styles.metaItem}>
                <FaMapMarkerAlt />
                <span>{location.name}</span>
              </div>
            )}
          </div>

          {/* Location Map */}
          {hasValidCoordinates && GOOGLE_MAPS_API_KEY && (
            <div className={styles.mapSection}>
              <a 
                href={getDirectionsUrl()} 
                target="_blank" 
                rel="noopener noreferrer"
                className={styles.mapContainer}
                title="Get directions"
              >
                <img
                  src={`https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=14&size=300x120&scale=2&markers=color:0xA4EB3F%7C${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}&style=feature:all%7Celement:labels%7Cvisibility:simplified&style=feature:road%7Celement:geometry%7Ccolor:0x444444&style=feature:landscape%7Celement:geometry%7Ccolor:0x1a1a1a&style=feature:poi%7Cvisibility:off&style=feature:water%7Celement:geometry%7Ccolor:0x0d0d0d`}
                  alt={`Map showing ${location?.name || 'event location'}`}
                  className={styles.mapImage}
                />
                <div className={styles.mapOverlay}>
                  <FaDirections />
                  <span>Get Directions</span>
                </div>
              </a>
            </div>
          )}

          {/* Live Score if applicable */}
          {isLive && nextEvent.liveSession?.score && (
            <div className={styles.liveScore}>
              <span>{nextEvent.liveSession.score.home}</span>
              <span className={styles.scoreDivider}>-</span>
              <span>{nextEvent.liveSession.score.away}</span>
            </div>
          )}

          {/* RSVP Section */}
          {!isLive && (
            <div className={styles.rsvpSection}>
              <span className={styles.rsvpLabel}>Your RSVP:</span>
              <div className={styles.rsvpButtons}>
                <button
                  className={`${styles.rsvpBtn} ${styles.going} ${userRsvp === 'going' ? styles.active : ''}`}
                  onClick={() => handleRsvp('going')}
                  disabled={rsvpLoading}
                >
                  <FaCheckCircle /> Going
                </button>
                <button
                  className={`${styles.rsvpBtn} ${styles.maybe} ${userRsvp === 'tbd' ? styles.active : ''}`}
                  onClick={() => handleRsvp('tbd')}
                  disabled={rsvpLoading}
                >
                  <FaQuestionCircle /> Maybe
                </button>
                <button
                  className={`${styles.rsvpBtn} ${styles.notGoing} ${userRsvp === 'notGoing' ? styles.active : ''}`}
                  onClick={() => handleRsvp('notGoing')}
                  disabled={rsvpLoading}
                >
                  <FaTimesCircle /> Can't Go
                </button>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className={styles.actionRow}>
            <button className={styles.viewBtn} onClick={handleViewEvent}>
              {isLive ? 'View Scorecard' : 'View Details'} <FaArrowRight />
            </button>
            <button className={styles.allEventsBtn} onClick={handleViewAllEvents}>
              All Events <FaArrowRight />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyNextUpWidget;

